// src/components/PdfWhiteboard/hooks/useStageInteraction.js
import { useState, useCallback, useEffect, useRef } from 'react';

const MIN_ZOOM = 0.1; // Allow zooming out further
const MAX_ZOOM = 5;
const ZOOM_SENSITIVITY = 1.1; // Adjust sensitivity as needed


const calculateFit = (stageContentDimensions, containerDimensions) => {
    if (!stageContentDimensions?.width || !containerDimensions?.width || stageContentDimensions.width === 0 || containerDimensions.width === 0) {
        return { scale: 1, pos: { x: 0, y: 0 } };
    }

    const scaleX = containerDimensions.width / stageContentDimensions.width;
    const scaleY = containerDimensions.height / stageContentDimensions.height;
    const scale = Math.min(scaleX, scaleY);

    const pos = {
        x: (containerDimensions.width - stageContentDimensions.width * scale) / 2,
        y: (containerDimensions.height - stageContentDimensions.height * scale) / 2,
    };

    return { scale, pos };
};

export const useStageInteraction = (stageRef, stageContentDimensions, containerDimensions, projectName) => {
    const [stageScale, setStageScale] = useState(1);
    const [stagePos, setStagePos] = useState({ x: 0, y: 0 });
    const [initialFit, setInitialFit] = useState({ scale: 1, pos: { x: 0, y: 0 } });
    const lastDist = useRef(0); // Store the distance between the two touches

    const lastProjectName = useRef(projectName);
    const hasInitializedRef = useRef(false);

    useEffect(() => {
        const { scale, pos } = calculateFit(stageContentDimensions, containerDimensions);
        setInitialFit({ scale, pos });

        // Only consider the fit "valid" when BOTH the content and the container
        // have real dimensions — otherwise the first render (container = 0x0)
        // would mark the view as initialized and the real fit never applies.
        const hasRealDims =
            stageContentDimensions?.width > 0 && containerDimensions?.width > 0;
        const isFirstValidFit = !hasInitializedRef.current && hasRealDims;
        const isNewProject = lastProjectName.current !== projectName;

        // Only reset the user's view if they change projects or on first load
        // This makes the viewport persistent across page turns within the same project.
        if (isNewProject || isFirstValidFit) {
            setStageScale(scale);
            setStagePos(pos);

            if (hasRealDims) {
                hasInitializedRef.current = true;
            }
            lastProjectName.current = projectName;
        }
    }, [
        stageContentDimensions?.width,
        stageContentDimensions?.height,
        containerDimensions?.width,
        containerDimensions?.height,
        projectName
    ]);

    const handleWheel = useCallback((e) => {
        e.evt.preventDefault();
        const stage = stageRef.current;
        if (!stage) return;

        const oldScale = stage.scaleX();
        const pointer = stage.getPointerPosition();
        if (!pointer) return; // Exit if pointer position is not available

        const mousePointTo = {
            x: (pointer.x - stage.x()) / oldScale,
            y: (pointer.y - stage.y()) / oldScale,
        };

        const direction = e.evt.deltaY > 0 ? -1 : 1; // -1 zoom out, 1 zoom in
        let newScale = direction > 0 ? oldScale * ZOOM_SENSITIVITY : oldScale / ZOOM_SENSITIVITY;


        // Apply Min/Max Zoom Constraints
        newScale = Math.max(MIN_ZOOM, Math.min(newScale, MAX_ZOOM));

        const newPos = {
            x: pointer.x - mousePointTo.x * newScale,
            y: pointer.y - mousePointTo.y * newScale,
        };

        setStageScale(newScale);
        setStagePos(newPos);
    }, [stageRef]);

    // --- Touch Handlers (Pinch Zoom) ---
    const handleTouchStart = useCallback((e) => {
        // Only preventDefault for multi-touch gestures
        if (e.evt.touches.length === 2) {
            e.evt.preventDefault(); // Only prevent for pinch gestures

            const touch1 = e.evt.touches[0];
            const touch2 = e.evt.touches[1];

            // Calculate initial distance between the touches
            lastDist.current = Math.sqrt(
                Math.pow(touch2.clientX - touch1.clientX, 2) +
                Math.pow(touch2.clientY - touch1.clientY, 2)
            );
        }
        // Don't preventDefault for single touch - let it bubble to shape handlers
    }, []);

    const handleTouchMove = useCallback((e) => {
        // PreventDefault is important to disable the browser's default behavior
        e.evt.preventDefault();
        const stage = stageRef.current;
        if (!stage) return;

        if (e.evt.touches.length === 2) {
            const touch1 = e.evt.touches[0];
            const touch2 = e.evt.touches[1];

            const newDist = Math.sqrt(
                Math.pow(touch2.clientX - touch1.clientX, 2) +
                Math.pow(touch2.clientY - touch1.clientY, 2)
            );

            if (lastDist.current === 0) return;

            // Calculate the center point between the two touches
            const centerX = (touch1.clientX + touch2.clientX) / 2;
            const centerY = (touch1.clientY + touch2.clientY) / 2;

            // Get the stage's bounding box to convert screen coordinates to stage coordinates
            const stageBox = stage.container().getBoundingClientRect();
            const centerPointTo = {
                x: (centerX - stageBox.left - stage.x()) / stage.scaleX(),
                y: (centerY - stageBox.top - stage.y()) / stage.scaleY(),
            };

            const oldScale = stage.scaleX();
            const scaleBy = newDist / lastDist.current;
            const newScale = Math.max(MIN_ZOOM, Math.min(oldScale * scaleBy, MAX_ZOOM));

            // Calculate new position to zoom around the center point
            const newPos = {
                x: centerX - stageBox.left - centerPointTo.x * newScale,
                y: centerY - stageBox.top - centerPointTo.y * newScale,
            };

            setStageScale(newScale);
            setStagePos(newPos);
            lastDist.current = newDist;
        }
    }, [stageRef]);

    const handleTouchEnd = useCallback(() => {
        // Reset the last distance when the touches end
        lastDist.current = 0;
    }, []);






    const resetView = useCallback(() => {
        setStageScale(initialFit.scale);
        setStagePos(initialFit.pos);
    }, [initialFit]);

    // Stage Drag Handlers (can be passed directly to Stage props)
    const handleStageDragEnd = useCallback((e) => {
        // Only update position if the stage itself was dragged
        if (e.target === stageRef.current) {
            setStagePos(e.target.position());
        }
    }, [stageRef]);


    return {
        stageScale,
        stagePos,
        setStageScale, // Expose if needed externally
        setStagePos,   // Expose if needed externally
        handleWheel,
        resetView,
        handleStageDragEnd,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        isMultiTouchActive: () => lastDist.current > 0,
    };
};
